<div class="flex flex-col gap-3">
  <!-- Bulk-edit bar -->
  <div
    *ngIf="bulkColumns.length"
    class="rounded-xl border border-[color-mix(in_srgb,var(--primary-500),white_85%)] overflow-hidden flex-shrink-0"
  >
    <!-- Title: status only — informs what's selected. -->
    <div
      class="flex items-center justify-between gap-3 px-4 py-4 flex-wrap"
      style="background-color: #ffffff;"
    >
      <span class="text-xs text-gray-600 flex items-center gap-2 flex-wrap">
        <ng-container *ngIf="selectedColumns.length; else rowStatus">
          <i class="pi pi-table text-[var(--primary-500)]"></i>
          <ng-container *ngIf="selectedColumns.length === 1">
            <strong class="text-[var(--primary-600)]">"{{ selectedColumns[0].header }}"</strong> selected — set a value below to apply it to every row, or use Clear.
          </ng-container>
          <ng-container *ngIf="selectedColumns.length > 1">
            <strong class="text-[var(--primary-600)]">{{ selectedColumns.length }} columns</strong> selected — set values below to apply them to every row, or use Clear.
          </ng-container>
        </ng-container>

        <ng-template #rowStatus>
          <i class="pi pi-pencil"></i>
          <ng-container *ngIf="selectedRows.length; else pickRowsHint">
            Set on <strong class="text-[var(--primary-600)]">{{ selectedRows.length }}</strong>
            selected {{ selectedRows.length === 1 ? 'row' : 'rows' }}:
          </ng-container>
          <ng-template #pickRowsHint>
            <span class="italic text-gray-500">Tick rows on the left to bulk-edit (or the header checkbox for all)</span>
          </ng-template>
        </ng-template>
      </span>

      <span
        *ngIf="hasEmptyRequired"
        class="text-xs font-semibold text-red-600 bg-red-50 border border-red-600 rounded-lg px-3 py-1 flex items-center gap-1.5 flex-shrink-0"
      >
        <i class="pi pi-exclamation-triangle"></i>
        Empty required fields need attention
      </span>
    </div>

    <!-- Middle: one editor per bulk-editable column, grouped into rows via
         `col.bulkRow` — each row's editors split that row's width evenly.
         Each editor is enabled/disabled independently — same conditional
         release as row selection. -->
    <div
      class="flex flex-col gap-3 px-4 py-3 border-t"
      style="background-color: #ffffff; border-color: color-mix(in srgb, var(--primary-500), white 80%);"
    >
      <div *ngFor="let rowCols of bulkRows; trackBy: trackByBulkRowIndex" class="grid gap-3" [style.grid-template-columns]="'repeat(' + rowCols.length + ', minmax(0, 1fr))'">
        <div *ngFor="let col of rowCols; trackBy: trackByColumnField">
          <app-input-dropdown
            *ngIf="col.type === 'dropdown'"
            [options]="col.options || emptyOptions"
            [optionLabel]="col.optionLabel || 'name'"
            [optionValue]="col.optionValue || 'id'"
            [placeholder]="col.header"
            [disabled]="!isBulkColumnEnabled(col)"
            [showClear]="true"
            [(ngModel)]="bulkValues[col.field]"
          ></app-input-dropdown>

          <app-input-dropdown-search
            *ngIf="col.type === 'dropdown-search'"
            [options]="col.options || emptyOptions"
            [optionLabel]="col.optionLabel || 'name'"
            [optionValue]="col.optionValue || 'id'"
            [minSearchLength]="col.minSearchLength || 0"
            [totalAvailable]="col.totalAvailable ?? null"
            [placeholder]="col.header"
            [disabled]="!isBulkColumnEnabled(col)"
            [showClear]="true"
            (filterChange)="col.onFilter && col.onFilter($event)"
            [(ngModel)]="bulkValues[col.field]"
          ></app-input-dropdown-search>

          <app-custom-datepicker
            *ngIf="col.type === 'date'"
            [placeholder]="col.header"
            [disabled]="!isBulkColumnEnabled(col)"
            [showClear]="true"
            [(ngModel)]="bulkValues[col.field]"
          ></app-custom-datepicker>
        </div>
      </div>
    </div>

    <!-- Footer: actions only. -->
    <div
      class="flex items-center justify-end gap-2 px-4 py-3 border-t"
      style="background-color: #ffffff; border-color: color-mix(in srgb, var(--primary-500), white 80%);"
    >
      <button
        pButton
        type="button"
        label="Clear"
        icon="pi pi-eraser"
        iconPos="right"
        class="font-bold text-sm shadow-sm transition-all px-6 py-2 bg-white text-gray-600 border border-[var(--primary-500)] hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
        [disabled]="!hasBulkTargets"
        (click)="clearBulk()"
      ></button>
      <button
        pButton
        type="button"
        label="Apply"
        icon="pi pi-check"
        iconPos="right"
        class="font-bold border-none text-sm shadow-sm transition-all px-6 py-2 disabled:opacity-50 disabled:pointer-events-none"
        [style]="{ 'background-color': 'var(--primary-500)', color: '#ffffff' }"
        [disabled]="!hasBulkTargets || !hasBulkValueSet"
        (click)="applyBulk()"
      ></button>
      <button
        pButton
        type="button"
        label="Delete"
        icon="pi pi-trash"
        iconPos="right"
        class="font-bold border-none text-sm shadow-sm transition-all px-6 py-2 !bg-red-600 hover:!bg-red-700 !text-white disabled:opacity-50 disabled:pointer-events-none disabled:hover:!bg-red-600"
        [disabled]="!selectedRows.length"
        (click)="requestDeleteSelected()"
      ></button>
    </div>
  </div>

  <!-- Grid — gets an extra outer ring when "select all" is active, on top of its own always-on border.
       No fixed height / internal scroll here on purpose — the table grows to
       its natural content height and the page itself scrolls (the app shell's
       main content area is already the scroll container), consistent with
       how the rest of the app scrolls full-page rather than in a nested
       viewport. -->
  <div
    #tableShell
    class="bg-white rounded-xl shadow-sm border border-gray-200 relative z-0 transition-shadow duration-150"
    [style.box-shadow]="isAllSelected ? '0 0 0 2px var(--primary-500)' : null"
  >
    <!-- Top horizontal scrollbar mirror — same pattern as app-table, so you
         don't have to scroll all the way down just to reach the bottom one. -->
    <div #topScrollbar class="top-scrollbar" (scroll)="onTopScroll()">
      <div #topScrollbarInner class="top-scrollbar-inner"></div>
    </div>

    <p-table
      [value]="visibleRows"
      [selection]="selectedRows"
      (selectionChange)="onSelectionChange($event)"
      [scrollable]="true"
      stripedRows
      styleClass="p-datatable-sm"
      [loading]="loading"
    >
      <ng-template pTemplate="header">
        <tr class="bg-white border-b border-gray-200 text-gray-700 uppercase text-xs tracking-wider h-14">
          <th *ngIf="showRowNumber" style="width: 3rem" class="pl-3 pr-0 border-none"></th>
          <th style="width: 2.75rem" class="px-3 border-none">
            <div class="flex justify-center">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </div>
          </th>
          <!-- ng-container per column so the toggle column (a plain *ngIf, not part
               of `columns`) can be interspersed right after a specific index instead
               of only ever at the very end — two structural directives can't sit on
               adjacent elements without this wrapper. -->
          <ng-container *ngFor="let col of columns; let i = index">
            <th
              [style.width]="col.width"
              class="px-3 py-3 border-none text-left select-none font-bold transition-colors duration-150"
              [style.background-color]="isColumnSelected(col) ? 'color-mix(in srgb, var(--primary-500), white 92%)' : null"
              [style.box-shadow]="headerColumnBorderShadow(col)"
            >
              <div class="flex items-center">
                <p-checkbox
                  class="compact-checkbox mr-1.5"
                  [class.select-all-mirror]="isAllSelected"
                  [ngModel]="isColumnSelected(col)"
                  (onChange)="toggleColumnSelection(col)"
                  [binary]="true"
                  pTooltip="Select to clear this field on every row"
                  tooltipPosition="top"
                  tooltipStyleClass="custom-tooltip"
                ></p-checkbox>

                <span class="font-bold">
                  {{ col.header }}<span *ngIf="col.required" class="text-red-500 ml-1">*</span>
                </span>

                <span class="cursor-pointer flex items-center group ml-2.5" (click)="onSort(col.field)">
                  <span class="flex flex-col space-y-[-4px]">
                    <i
                      class="pi pi-caret-up text-xs transition-all duration-200"
                      [ngClass]="{
                        'text-[var(--primary-500)] scale-125 font-bold': sortField === col.field && sortOrder === 1,
                        'text-gray-300 group-hover:text-gray-400': sortField !== col.field || sortOrder !== 1
                      }"
                    ></i>
                    <i
                      class="pi pi-caret-down text-xs transition-all duration-200"
                      [ngClass]="{
                        'text-[var(--primary-500)] scale-125 font-bold': sortField === col.field && sortOrder === -1,
                        'text-gray-300 group-hover:text-gray-400': sortField !== col.field || sortOrder !== -1
                      }"
                    ></i>
                  </span>
                </span>
              </div>
            </th>
            <th
              *ngIf="showToggleColumn && i === toggleColumnAfterIndex"
              class="toggle-column-cell cursor-pointer select-none"
              (click)="onToggleColumnClick()"
              [pTooltip]="toggleExpanded ? 'Show fewer columns' : 'Show more columns'"
              tooltipPosition="top"
              tooltipStyleClass="custom-tooltip"
            >
              <div class="flex justify-center">
                <i class="pi text-sm text-gray-500" [ngClass]="toggleExpanded ? 'pi-minus' : 'pi-plus'"></i>
              </div>
            </th>
          </ng-container>
          <!-- toggleColumnAfterIndex null, or >= the last column (collapsed, no
               extra columns to insert after) — render at the very end instead. -->
          <th
            *ngIf="showToggleColumn && (toggleColumnAfterIndex === null || toggleColumnAfterIndex >= columns.length)"
            class="toggle-column-cell cursor-pointer select-none"
            (click)="onToggleColumnClick()"
            [pTooltip]="toggleExpanded ? 'Show fewer columns' : 'Show more columns'"
            tooltipPosition="top"
            tooltipStyleClass="custom-tooltip"
          >
            <div class="flex justify-center">
              <i class="pi text-sm text-gray-500" [ngClass]="toggleExpanded ? 'pi-minus' : 'pi-plus'"></i>
            </div>
          </th>
        </tr>
      </ng-template>

      <!-- Each row is its own OnPush component (EditableTableRowComponent) —
           editing a cell in one row only re-checks that row, not all of them.
           See PARTE-2-OTIMIZACAO-TABELA-review-calls.md for why. -->
      <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
        <tr
          app-editable-table-row
          [row]="row"
          [columns]="columns"
          [selectedColumns]="selectedColumns"
          [emptyOptions]="emptyOptions"
          [rowIndex]="rowIndex"
          [showRowNumber]="showRowNumber"
          [showToggleColumn]="showToggleColumn"
          [toggleColumnAfterIndex]="toggleColumnAfterIndex"
          [loadingFields]="loadingFields"
          [isSelected]="isRowSelected(row)"
          [isAllSelected]="isAllSelected"
          [prevRowSelected]="rowIndex > 0 && isRowSelected(visibleRows[rowIndex - 1])"
          [nextRowSelected]="rowIndex < visibleRows.length - 1 && isRowSelected(visibleRows[rowIndex + 1])"
          [isLastRow]="rowIndex === visibleRows.length - 1"
          (cellValueChanged)="onCellValueChanged(row, $event.col, $event.value)"
        ></tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr *ngIf="!loading">
          <td
            [attr.colspan]="columns.length + (showRowNumber ? 2 : 1) + (showToggleColumn ? 1 : 0)"
            class="text-center p-8 text-gray-400"
          >
            <div class="flex flex-col items-center justify-center gap-2 py-8">
              <i class="pi pi-inbox text-4xl opacity-50"></i>
              <span class="text-sm">{{ emptyMessage }}</span>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <div
      *ngIf="showFooter"
      class="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0 rounded-b-xl"
    >
      <ng-content></ng-content>
    </div>
  </div>
</div>
